import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { useUser } from "../../../hooks/useUser";
import { IconInfo } from "../../../utils/icons";
import { isInternal, isRMN } from "../../../utils";

export const formatThousands = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return "";
  const [intPart, decPart] = String(raw).split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${withCommas}.${decPart}` : withCommas;
};

// Brand-facing explanation shown in place of the numeric score. Deliberately
// free of the formula / raw percentages — brands see what Expert IQ is, not how
// it's computed (that stays on the internal-only Algo/Ideal tooltips).
const EXPERT_IQ_INFO = (
  <span style={{ fontSize: 12, lineHeight: 1.5 }}>
    <strong>Expert IQ</strong>
    <br />
    These experts were matched and ranked for your products using Expert IQ,
    Desire's system for scoring verified creators on how well their area of
    focus, professions, and experience fit what you're creating. Your account
    manager reviews and curates the final list.
  </span>
);

export const ScoreRatePills = ({
  expert,
  showRequired = false,
  className = "",
  editable = false,
  scoreValue,
  onScoreChange,
  rateValue,
  onRateChange,
  savingScore = false,
  savingRate = false,
  hideRate = false,
  // Provenance: true when the score is a manual override rather than the
  // Expert IQ algorithm default. In editable mode it's passed explicitly; in
  // read-only mode it's read off the BrandProjectPro row.
  scoreOverridden = undefined,
  // Reset-to-algo handler (editable mode only). When provided and the score is
  // overridden, a "Reset" link is shown that reverts to the algorithm value.
  onResetScore = undefined,
  // Read-only display override — lets callers show an aggregate score (e.g. the
  // algorithm best-fit) for pros without a stored BrandProjectPro.score yet.
  displayScore = undefined,
  // Hard override to suppress the numeric score regardless of role — used on the
  // public share link so it always renders the clean brand view.
  forceHideScore = false,
}) => {
  const { role, user } = useUser();
  const hasDisplayScore = displayScore !== undefined && displayScore !== null;
  const readOnlyScore = hasDisplayScore
    ? displayScore
    : expert?.projects?.[0]?.BrandProjectPro?.score;
  const scoreRaw = editable ? scoreValue : readOnlyScore;
  const rateRaw = editable
    ? rateValue
    : expert?.projects?.[0]?.BrandProjectPro?.rate;

  const overridden = editable
    ? !!scoreOverridden
    : !!expert?.projects?.[0]?.BrandProjectPro?.score_overridden;

  const numericScore =
    scoreRaw !== undefined && scoreRaw !== null && scoreRaw !== ""
      ? parseFloat(scoreRaw)
      : null;
  const parsedRate = parseFloat(rateRaw);
  const hasRate = editable || (Number.isFinite(parsedRate) && parsedRate !== 0);
  const showRate = hasRate && !hideRate && isInternal(role);

  // The raw Expert IQ score is internal-only. Brand admins, brand team members,
  // RMN brands, and public (unauthenticated) viewers see the explanation icon
  // instead of the number.
  const showScore = isInternal(role) && !isRMN(user) && !forceHideScore;

  if (!editable && numericScore === null && !hasRate) return null;

  const ringScore = numericScore !== null ? numericScore : 0;
  const ringColor =
    ringScore > 80 ? "#5A9E6F" : ringScore > 30 ? "#AB854A" : "#D4725C";
  const r = 15;
  const circ = 2 * Math.PI * r;

  const rateInputRef = React.useRef(null);
  const pendingCaretDigitsRef = React.useRef(null);

  const handleRateChange = (event) => {
    const { value, selectionStart } = event.target;
    pendingCaretDigitsRef.current = value
      .slice(0, selectionStart ?? value.length)
      .replace(/\D/g, "").length;
    onRateChange?.(event);
  };

  React.useLayoutEffect(() => {
    const input = rateInputRef.current;
    const digitsBeforeCaret = pendingCaretDigitsRef.current;
    pendingCaretDigitsRef.current = null;
    if (!input || digitsBeforeCaret === null || document.activeElement !== input) {
      return;
    }
    let caret = 0;
    let digitsSeen = 0;
    while (caret < input.value.length && digitsSeen < digitsBeforeCaret) {
      if (/\d/.test(input.value[caret])) digitsSeen += 1;
      caret += 1;
    }
    input.setSelectionRange(caret, caret);
  });

  return (
    <div className={`flex flex-wrap items-center gap-[6px] min-w-0 ${className}`}>
      {showScore && (
        <>
          <div
            className="bg-surface-2 border border-border-token rounded-[10px] h-[34px] px-[6px] flex items-center justify-between gap-[4px] flex-1 basis-[104px] min-w-[104px] max-w-[130px] transition-colors focus-within:border-accent"
          >
            <span className="text-[9px] font-semibold uppercase tracking-[0.6px] text-text-2 font-circular shrink-0">
              Score
              {showRequired && <span className="text-[#D4725C] ml-[1px]">*</span>}
            </span>
            <div className="flex items-baseline gap-[1px] min-w-0">
              {editable ? (
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={scoreValue ?? ""}
                  onChange={onScoreChange}
                  placeholder="0"
                  className="field-input bg-transparent border-none outline-none text-[14px] font-bold text-text-1 font-circular leading-none p-0 w-[26px] text-right min-w-0"
                />
              ) : (
                <span className="text-[14px] font-bold text-text-1 font-circular leading-none truncate">
                  {numericScore}
                </span>
              )}
              <span className="text-[11px] font-semibold text-muted font-circular">
                %
              </span>
            </div>
            <div className="relative w-[18px] h-[18px] shrink-0">
              <svg width="18" height="18" viewBox="0 0 36 36">
                <circle
                  cx="18"
                  cy="18"
                  r={r}
                  fill="none"
                  style={{ stroke: "var(--track)" }}
                  strokeWidth="3"
                />
                <circle
                  cx="18"
                  cy="18"
                  r={r}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray={circ}
                  strokeDashoffset={circ * (1 - ringScore / 100)}
                  style={{
                    transform: "rotate(-90deg)",
                    transformOrigin: "center",
                    transition: "stroke-dashoffset 0.4s ease, stroke 0.3s ease",
                  }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-text-1 font-circular leading-none">
                {ringScore}
              </div>
            </div>
          </div>

          {/* Provenance: "Auto" when the score is the Expert IQ default, "Edited"
              (with a Reset link in editable mode) when manually overridden. */}
          {numericScore !== null && !overridden && (
            <span
              title="Score from the Expert IQ algorithm"
              className="text-[9px] font-semibold uppercase tracking-[0.4px] text-text-2 font-circular shrink-0"
            >
              Auto
            </span>
          )}
          {numericScore !== null && overridden && editable && onResetScore && (
            <button
              type="button"
              onClick={onResetScore}
              disabled={savingScore}
              title="Revert to the Expert IQ algorithm score"
              className="text-[10px] font-semibold text-accent-text underline font-circular shrink-0 hover:opacity-80 disabled:opacity-50"
            >
              Reset
            </button>
          )}
          {numericScore !== null &&
            overridden &&
            !(editable && onResetScore) && (
              <span className="text-[9px] font-semibold uppercase tracking-[0.4px] text-muted font-circular shrink-0">
                Edited
              </span>
            )}
        </>
      )}

      {/* Brand / public view: the raw score is hidden — show the Expert IQ
          explanation in its place. */}
      {!showScore && numericScore !== null && (
        <div className="flex items-center gap-[6px] min-w-0">
          <span className="text-[9px] font-semibold uppercase tracking-[0.6px] text-text-2 font-circular shrink-0">
            Expert IQ
          </span>
          <Tooltip placement="top" arrow title={EXPERT_IQ_INFO}>
            <span className="inline-flex items-center cursor-help text-text-2 shrink-0">
              <IconInfo width={12} height={12} stroke="currentColor" />
            </span>
          </Tooltip>
        </div>
      )}

      {showRate && (
        <div
          className="bg-surface-2 border border-border-token rounded-[10px] h-[34px] px-[6px] flex items-center justify-between gap-[4px] flex-1 basis-[104px] min-w-[104px] max-w-[130px] transition-colors focus-within:border-accent"
        >
          <span className="text-[9px] font-semibold uppercase tracking-[0.6px] text-text-2 font-circular shrink-0">
            Rate
          </span>
          <div className="flex items-baseline gap-[1px] min-w-0">
            <span className="text-[11px] font-semibold text-muted font-circular">
              $
            </span>
            {editable ? (
              <input
                type="text"
                inputMode="numeric"
                ref={rateInputRef}
                value={formatThousands(rateValue)}
                onChange={handleRateChange}
                placeholder="0"
                className="field-input bg-transparent border-none outline-none text-[14px] font-bold text-text-1 font-circular leading-none p-0 text-right min-w-0 pb-[3px]"
              />
            ) : (
              <span className="text-[14px] font-bold text-text-1 font-circular leading-none truncate">
                {formatThousands(parsedRate)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ScoreRatePills;
